<template>
  <div class="w-[40%] white-background bg-white dark:bg-slate-900 z-[7]">
    <div class="flex-end mb-4">
      <select v-model="filter" @change="onFilterChange" class="select">
        <option value="Today">{{ $t('PAYMENTS.TODAY') }}</option>
        <option value="week">{{ $t('PAYMENTS.LAST_WEEK') }}</option>
        <option value="Month">{{ $t('PAYMENTS.LAST_MONTH') }}</option>
        <option value="Custom">{{ $t('PAYMENTS.CUSTOM') }}</option>
      </select>

      <!-- <div v-if="filter === 'custom'" class="mt-2 flex gap-2">
        <input type="date" v-model="customStart" class="input" />
        <input type="date" v-model="customEnd" class="input" />
      </div> -->
    </div>
    <div v-if="filter === 'Custom'" class="mt-2 flex-end gap-2">
      <div class="date-picker">
        <date-picker
          :range="true"
          :confirm="true"
          :clearable="false"
          :editable="false"
          :confirm-text="confirmText"
          :placeholder="placeholder"
          :value="value"
          @change="handleChange"
          v-model="selectedDate"
        />
      </div>
    </div>
    <div v-if="!uiFlags.isFetchingItem">
      <apexchart
        type="line"
        height="300"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
    <div class="fixed-height" v-else-if="uiFlags.isFetchingItem">
      <loading-state
        v-if="uiFlags.isFetchingItem"
        :message="emptyStateMessage"
      />
    </div>
  </div>
</template>
<script>
import ApexChart from 'vue-apexcharts';
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';
import LoadingState from 'dashboard/components/widgets/LoadingState';
import DatePicker from 'vue2-datepicker';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default {
  name: 'ChartWithFilter',
  components: {
    apexchart: ApexChart,
    LoadingState,
    DatePicker,
  },
  props: {
    payments: {
      type: Array,
      required: true,
    },
    selectedFilters: {
      type: String,
      default: 'Today'
    },
    selectedDateCustom: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      filter: 'Today',
      selectedDate: null,
      emptyStateMessage: 'Loading Graph',
      confirmText: 'Select Date Range',
      placeholder: 'Select date range',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'payments/getUIFlags',
    }),
    graphData() {
      const data = this.$store.getters['payments/graphData'];
      return data.data;
    },
    filteredData() {
      let start, end;
      const now = dayjs();

      if (this.filter === 'Today') {
        start = now.startOf('day');
        end = now.endOf('day');
      } else if (this.filter === 'week') {
        start = now.subtract(6, 'day').startOf('day');
        end = now.endOf('day');
      } else if (this.filter === 'Month') {
        start = now.subtract(1, 'month').startOf('day');
        end = now.endOf('day');
      } else if (this.filter === 'Custom') {
        if (!this.selectedDate || this.selectedDate.length !== 2) return [];
        start = dayjs(this.selectedDate[0]);
        end = dayjs(this.selectedDate[1]);
      }

      return this.graphData.filter(entry => {
        const date = dayjs(entry.date);
        return date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
      });
    },
    chartSeries() {
      return [
        {
          name: 'Amount',
          data: this.filteredData.map(entry => Number(entry.value)),
        },
      ];
    },
    chartOptions() {
      return {
        chart: { id: 'filtered-line-chart' },
        xaxis: { categories: this.filteredData.map(entry => entry.date) },
        title: {
          text: this.filter,
          align: 'left',
          offsetY: 0,
          style: { fontSize: '16px', fontWeight: 'bold' },
        },
        stroke: { curve: 'smooth' },
      };
    },
  },
  watch: {
    selectedFilters: {
      immediate: true,
      handler() {
        this.setInitialFilter();
      }
    },
    selectedDateCustom(newVal) {
      // if (this.selectedFilters === 'Custom' && newVal && newVal.length === 2) {
      //   this.selectedDate = newVal;
      //   this.handleChange(newVal);
      // }
        if (this.selectedFilters === 'custom') {
        if (!newVal || newVal.length !== 2) {
          this.selectedDate = null; // ⬅️ Reset local state
        } else {
          this.selectedDate = newVal;
          this.handleChange(newVal);
        }
  }
    }
  },
  methods: {
    formatDate(date) {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    async graphvalue(start, end) {
      const payload = {
        start_date: start || null,
        end_date: end || null,
      };
      await this.$store.dispatch('payments/GraphView', payload);
    },
    onFilterChange() {
      const today = new Date();
      let startDate, endDate;

      if (this.filter === 'Today') {
        startDate = endDate = today;
      } else if (this.filter === 'week') {
        endDate = today;
        startDate = new Date();
        startDate.setDate(today.getDate() - 6);
      } else if (this.filter === 'Month') {
        endDate = today;
        startDate = new Date();
        startDate.setMonth(today.getMonth() - 1);
      } else {
        return; // Don't handle 'custom' here
      }

      const formattedStart = this.formatDate(startDate);
      const formattedEnd = this.formatDate(endDate);

      this.graphvalue(formattedStart, formattedEnd);
    },
    handleChange(dates) {
      if (!dates || dates.length !== 2) return;

      const formattedStart = this.formatDate(dates[0]);
      const formattedEnd = this.formatDate(dates[1]);

      this.selectedDate = dates;
      this.graphvalue(formattedStart, formattedEnd);
    },
    setInitialFilter() {
        const rawFilter = this.selectedFilters !== null ? this.selectedFilters : this.filter;

        this.filter = rawFilter === 'lastWeek' ? 'week'
                    : rawFilter === 'lastMonth' ? 'Month'
                    : rawFilter === 'custom' ? 'Custom'
                    : 'Today';

        // if (this.filter === 'Custom') {
        //   if (this.selectedDateCustom && this.selectedDateCustom.length === 2) {
        //     this.selectedDate = this.selectedDateCustom;
        //     this.handleChange(this.selectedDateCustom);
        //   } else {
        //     // stay on 'custom', don't fetch data yet
        //     // console.log('[Custom] Waiting for date range...');
        //   }
        // } else {
        //   this.onFilterChange();
        // }

              if (this.filter === 'Custom') {
          if (this.selectedDateCustom && this.selectedDateCustom.length === 2) {
            this.selectedDate = this.selectedDateCustom;
            this.handleChange(this.selectedDateCustom);
          } else {
            this.selectedDate = null;
          }
        } else {
          this.onFilterChange(); // ✅ Fetch initial graph data for Today, week, or Month
        }
      }

  }
};
</script>

  <style scoped>
.btn {
  background: #3b82f6;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.btn:hover {
  background: #2563eb;
}
.input {
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.white-background {
  /* background-color: #fff; */
  border-radius: 5px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  padding: 15px 10px;
  border: 1px solid;
}
.select {
  width: 23%;
}
.fixed-height {
  height: 300px;
}
</style>
  